import { createSelector } from 'reselect';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { secondsToTimestamp } from '../../api';
import { getQueueMode } from '../../api/queue-control';
import { ContactBasic, EntityType, getKey, Identifier, QueueStats, Shortcut, Extension } from '../../model';
import { isDefined } from '../../type-utils';
import { Actions, StoreState } from '../types';
import { getExtensionInfos } from './queueAgents';
import { createArrayCache } from '../../data-utils';

export type ShortcutData = (
  { entityType: EntityType.Contact } & ContactBasic |
  { entityType: EntityType.Extension } & Identifier |
  { entityType: EntityType.Queue } & QueueStats
) & { groupListId: string, sort: number }

function shortcuts(state: ShortcutData[] = [], action: Actions): ShortcutData[] {
  switch (action.type) {
    case getType(actions.setConfig): {
      return action.payload.shortcuts.map<ShortcutData | undefined>(({ entityId, serverName, description, entityType, groupListId, nonIdentifyingData, data }) => {
        if (!data) { return undefined }
        const sort = nonIdentifyingData ? parseInt(nonIdentifyingData) : -1

        if (entityType === EntityType.Contact) {
          const [regionId, extensionNumber, home, business, mobile, email] = data
          return {
            entityType,
            sort,
            groupListId,
            id: entityId,
            server: serverName,
            description,
            regionId,
            extensionNumber,
            home,
            business,
            mobile,
            email,
          }
        }

        if (entityType === EntityType.Extension) {
          return {
            entityType,
            sort,
            groupListId,
            id: entityId,
            server: serverName,
          }
        }

        if (entityType === EntityType.Queue) {
          const [icon, mode, status, count, time, agents, answer, abandoned, other, agentStatus, virtual, ansQTimeTotal, abnQTimeTotal] = data
          return {
            entityType,
            sort,
            groupListId,
            type: 'queue',
            id: entityId,
            server: serverName,
            description: description,
            voicemailCount: 0,
            icon: parseInt(icon),
            mode: getQueueMode(parseInt(mode)),
            status: status,
            count: parseInt(count),
            time: time !== '' ? secondsToTimestamp(parseInt(time)) : null,
            agents: parseInt(agents),
            answer: parseInt(answer),
            abandoned: parseInt(abandoned),
            other: parseInt(other),
            agentStatus: Math.round(parseInt(agentStatus)),
            virtual: virtual === '1',
            ansQTimeTotal: parseInt(ansQTimeTotal),
            abnQTimeTotal: parseInt(abnQTimeTotal),
            toaster: parseInt(icon),
            hide: false,
          }
        }

        return undefined
      }).filter(isDefined)
    }

    case (getType(actions.shortcutDeleteEntity)): {
      return state.filter(x => x.groupListId !== action.payload)
    }

    case (getType(actions.shortcutUpdateEntity)): {
      return [
        ...state.filter(x => x.groupListId !== action.payload.groupListId),
        action.payload,
      ]
    }
  }

  return state
}

export default shortcuts

function getShortcuts(state: StoreState) { return state.shortcuts }

const shortCutExtensions = createArrayCache<Extension | undefined>()

export const selectShortcuts = createSelector(
  getShortcuts,
  (state: StoreState) => {
    const shortcuts = getShortcuts(state)
    const extensions = getExtensionInfos(state)

    const data = shortcuts.length > 0 ? shortcuts.filter(s => s.entityType === EntityType.Extension).map(x => {
      return extensions[getKey(x)]
    }).filter(z => z !== undefined) : []

    return shortCutExtensions(shortcuts.length === 0 || !data ? [] : data, 'shortcuts')
  },
  (state: StoreState) => {
    return state.queues
  },
  (shortcuts, extensions, queues): Shortcut[] => {
    return shortcuts.map(s => {
      if (s.entityType === EntityType.Extension) {
        const e = extensions.find(d => d !== undefined && d.id === s.id && d.server === s.server)
        return e && {
          ...s,
          ...e,
        }
      } else if (s.entityType === EntityType.Queue) {
        const allQueues = queues?.queues
        if (!allQueues) return s

        const queueStats = allQueues[getKey({ id: s.id, server: s.server})]
        if (!queueStats) return s

        return queueStats && {
          ...s,
          ...queueStats,
        }
      } else {
        return s
      }
    }).filter(isDefined).sort((s1, s2) => s1.sort - s2.sort)
  },
)

export const selectabc = createSelector((s: StoreState) => s.shortcuts, (shortcuts) => {

})
